import { createSlice } from "@reduxjs/toolkit";
import { WritableDraft } from "@reduxjs/toolkit";

import { PROJECT_ID_FIELD } from "Constants/constants";
import { setDeep } from "Libs/objectAccess";
import {
  UpdateProjectReturnPayload,
  ProjectStateType,
  LoadOneProjectPayloadReturn,
  loadProjectSuccess,
  loadAllProjects,
  subscribe,
  loadOneProject,
  loadOneProjectHelper,
  reloadProject,
  updateProject,
  ReloadProjectReturnPayload,
  getOrganizationDescriptionIdFromProject,
  loadExtendedAccess,
  OrgIdWithProjectIds
} from "Reducers/project/project";

import type { Project } from "@packages/client";

const initialState: ProjectStateType = {
  all: [],
  data: {},
  list: {},
  extendedAccess: [],
  loadingExtendedAccess: false,
  loadedList: false,
  loading: false,
  loadingList: false,
  orgByProjectId: {},
  projectLoadingError: "",
  projectUpdateError: ""
};
// For Loading, Updating, and Reloading
const projectSuccessHandler = (
  payload:
    | LoadOneProjectPayloadReturn
    | UpdateProjectReturnPayload
    | ReloadProjectReturnPayload,
  state: WritableDraft<ProjectStateType>
) => {
  const { project, projectMe, organizations } = payload;
  const projectId = project[PROJECT_ID_FIELD];

  // When the project's organization can't be resolved (e.g. Magento, where
  // organizations are disabled), keep the project under the organization it
  // was requested with, or the one already stored for it. Storing it under ""
  // hides it from every selector keyed by the URL's organization, which leaves
  // the navigation empty (CON-1859).
  const orgName =
    getOrganizationDescriptionIdFromProject(
      projectMe || project,
      organizations
    ) ||
    ("organizationId" in payload && payload.organizationId) ||
    state.orgByProjectId[projectId] ||
    "";

  let updatedLocalProject: Project = project;
  if (projectMe) {
    updatedLocalProject = project.updateLocal({
      ...project,
      owner_info: projectMe?.owner_info,
      organization_id: projectMe?.organization_id,
      plan_uri: projectMe?.plan_uri,
      region_label: projectMe?.region_label,
      plan: projectMe?.plan,
      subscription_id: projectMe?.subscription_id
    });
  }

  state.loading = false;
  state.projectLoadingError = "";
  state.data = {
    [orgName]: {
      [projectId]: updatedLocalProject
    }
  };
  state.orgByProjectId = {
    [projectId]: orgName
  };
};

const projectReducer = createSlice({
  name: "app/project/index",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadExtendedAccess.pending, state => {
        state.loadingExtendedAccess = true;
      })
      .addCase(loadExtendedAccess.rejected, state => {
        state.loadingExtendedAccess = false;
      })
      .addCase(loadExtendedAccess.fulfilled, (state, action) => {
        state.loadingExtendedAccess = false;
        state.extendedAccess = action.payload.extendedAccess;
      });

    builder
      // LOAD PROJECT SUCCESS
      .addCase(loadProjectSuccess.fulfilled, (state, action) => {
        const { project, organizationId } = action.payload;

        const projectId: string = project[PROJECT_ID_FIELD];
        setDeep(state, ["data", organizationId, projectId], project);
        state.loading = false;
        setDeep(state, ["orgByProjectId", projectId], organizationId);
        state.projectLoadingError = "";
      })
      // LOAD ALL PROJECTS
      .addCase(loadAllProjects.pending, state => {
        state.loadingList = true;
      })
      .addCase(loadAllProjects.fulfilled, (state, action) => {
        const { projects, organizations } = action.payload;

        const newList = projects.reduce<OrgIdWithProjectIds>(
          (organizationsProjects, project) => {
            const orgName =
              getOrganizationDescriptionIdFromProject(project, organizations) ??
              "";

            if (!organizationsProjects[orgName]) {
              organizationsProjects[orgName] = {};
            }

            organizationsProjects[orgName][project[PROJECT_ID_FIELD]] = project;

            return organizationsProjects;
          },
          {}
        );

        state.loadingList = false;
        state.loadedList = true;
        state.list = newList;
        state.all = projects;
      })
      .addCase(loadAllProjects.rejected, (state, action) => {
        state.loadingList = false;
        state.loading = false;
        state.projectLoadingError = action.payload;
      })
      // SUBSCRIBE
      .addCase(subscribe.rejected, (state, action) => {
        state.projectLoadingError = action.payload?.error;
      })
      // LOAD ONE PROJECT
      .addCase(loadOneProject.pending, state => {
        state.projectLoadingError = "";
        state.loading = true;
      })
      // --> takes the place of "loadOneProject.fulfilled" IF "loadOneProject.fulfilled" returned a payload
      .addCase(loadOneProjectHelper.fulfilled, (state, action) => {
        projectSuccessHandler(action.payload, state);
      })
      .addCase(loadOneProject.rejected, (state, action) => {
        state.loading = false;
        state.projectLoadingError = action.payload;
      })
      // RELOAD PROJECTS
      .addCase(reloadProject.pending, state => {
        state.loading = true;
      })
      .addCase(reloadProject.fulfilled, (state, action) => {
        projectSuccessHandler(action.payload, state);
      })
      .addCase(reloadProject.rejected, (state, action) => {
        state.loading = false;
        state.projectLoadingError = action.payload;
      })
      // UPDATE PROJECT
      .addCase(updateProject.pending, state => {
        state.loading = true;
        state.projectUpdateError = "";
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        projectSuccessHandler(action.payload, state);
      })
      .addCase(updateProject.rejected, (state, { payload }) => {
        state.loading = false;
        state.projectUpdateError = payload;
      });
  }
});

export default projectReducer.reducer;
